import { getErrors } from '@/store/common/error-handlers';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { selectProfileData } from '@/store/phones/sagas';
import {
  all,
  call,
  put,
  select,
  StrictEffect,
  take,
  takeLeading,
} from 'redux-saga/effects';
import { RootState } from '@/store/store';
import { accountApi } from '@/api';
import { npiActions as actions } from './slice';
import { AccountDetails } from '@/types';
import { ValidationErrorType } from '@/types/ValidationError';
import { client } from '@/api/client/ApiClient';
import { showError500OrUnknownToast } from '@/store/common/showError500OrUnknownToast';
import { RequiredAny } from 'ui-kit/lib/types';
const { setErrors, setIsFetching, setNpiInfo, setRequestSuccess } = actions;
export function* npiSaga() {
  yield all([
    takeLeading(actions.requestAccountNpiData, requestAccountNpiData),
    takeLeading(actions.requestNPIInfo, requestNPIInfo),
    takeLeading(actions.requestConfirm, requestConfirm),
  ]);
}
function* waitFor(selector: RequiredAny): RequiredAny {
  if (yield select(selector)) return;

  while (true) {
    yield take('*');
    if (yield select(selector)) return;
  }
}

function* requestAccountNpiData(): Generator<StrictEffect, void> {
  yield put(setIsFetching(true));

  try {
    yield call(waitFor, selectProfileData);
    const account: AccountDetails = yield select(selectProfileData);
    if (account.npi) {
      const npi: Awaited<ReturnType<typeof accountApi.getAccountNpi>> =
        yield call(accountApi.getAccountNpi);
      yield put(setNpiInfo({ confirmed: true, npi, step: 'processed' }));
    } else {
      throw { response: { data: { error: 'NPI_NOT_FOUND' } } } as AxiosError;
    }
  } catch (e: RequiredAny) {
    if (e.response?.data?.error === 'NPI_NOT_FOUND') {
      yield call(checkIfNPIIsInProcess);
      return;
    }
    showError500OrUnknownToast(e);
  } finally {
    yield put(setIsFetching(false));
  }
}
function* checkIfNPIIsInProcess() {
  try {
    yield call(accountApi.checkNPIIsBeingProcessed);
    yield put(setNpiInfo({ confirmed: false, npi: null, step: 'init' }));
  } catch (e: RequiredAny) {
    if (e.response?.status === 409) {
      yield put(
        setNpiInfo({ confirmed: false, npi: null, step: 'processing' })
      );
    } else {
      showError500OrUnknownToast(e);
    }
  } finally {
    yield put(setIsFetching(false));
  }
}
function* requestNPIInfo({
  payload,
}: PayloadAction<string>): Generator<StrictEffect, void> {
  yield put(setIsFetching(true));
  try {
    const {
      data: npi,
    }: Awaited<
      ReturnType<typeof client.verifyAccountMedicareProviderRegistration>
    > = yield call(client.verifyAccountMedicareProviderRegistration, {
      npi: payload,
    });
    yield put(setNpiInfo({ confirmed: false, npi, step: 'registration' }));
  } catch (e) {
    const errors = getErrors(e);

    if (errors.common === ValidationErrorType.NPI_TYPE_INCORRECT) {
      errors.npi = ValidationErrorType.NPI_TYPE_INCORRECT_INDIVIDUAL;
      delete errors.common;
    }
    if (errors.common) {
      showError500OrUnknownToast(e);
    }
    yield put(setErrors(errors));
  } finally {
    yield put(setIsFetching(false));
  }
}

function* requestConfirm(): Generator<StrictEffect, void> {
  const { npiNumber } = yield select((rootState: RootState) => rootState.npi);
  yield put(setIsFetching(true));
  try {
    yield call(accountApi.confirmNPIInfo, npiNumber);
    yield put(setNpiInfo({ confirmed: false, npi: null, step: 'processing' }));
    yield put(setRequestSuccess(true));
  } catch (e) {
    const errors = getErrors(e);
    if (errors.common) {
      showError500OrUnknownToast(e);
    }
    yield put(setErrors(errors));
  } finally {
    yield put(setIsFetching(false));
  }
}
